export type PortalDashboardSegment = {
  key: string;
  label: string;
  count: number;
  color: string;
};

export type PortalDashboardProgressPoint = {
  monthLabel: string;
  plannedPercent: number;
  actualPercent: number;
};

export type PortalDashboardMilestone = {
  id: string;
  day: string;
  month: string;
  title: string;
  projectName: string;
  statusLabel: string;
  statusTone: 'validation' | 'progress' | 'planned';
};

export type PortalDashboardFeaturedProject = {
  id: string;
  name: string;
  summary: string;
  progressPercent: number;
  statusLabel: string;
  statusTone: 'progress' | 'analysis' | 'done';
  iconTone: 'blue' | 'green' | 'orange' | 'purple' | 'sky';
};

export type PortalDashboardUpdate = {
  id: string;
  title: string;
  detail: string;
  whenLabel: string;
  tone: 'success' | 'info' | 'warning' | 'neutral';
};

export type PortalDashboardRequest = {
  id: string;
  number: string;
  title: string;
  projectName: string;
  typeLabel: string;
  statusLabel: string;
  statusTone: 'approved' | 'progress' | 'analysis' | 'answered';
  dateLabel: string;
};

export type PortalDashboardData = {
  searchPlaceholder: string;
  dateRangeLabel: string;
  projectFilterLabel: string;
  activeProjects: {
    total: number;
    deltaLabel: string;
  };
  activities: {
    total: number;
    segments: PortalDashboardSegment[];
  };
  deliveries: {
    total: number;
    segments: PortalDashboardSegment[];
  };
  requests: {
    total: number;
    segments: PortalDashboardSegment[];
  };
  progressSeries: PortalDashboardProgressPoint[];
  progressTooltip: {
    monthLabel: string;
    plannedPercent: number;
    actualPercent: number;
  };
  milestones: PortalDashboardMilestone[];
  featuredProjects: PortalDashboardFeaturedProject[];
  updates: PortalDashboardUpdate[];
  recentRequests: PortalDashboardRequest[];
};

export const portalDashboardMock: PortalDashboardData = {
  searchPlaceholder: 'Buscar projetos, atividades, documentos...',
  dateRangeLabel: '01/10/2026 - 31/10/2026',
  projectFilterLabel: 'Todos os projetos',
  activeProjects: {
    total: 6,
    deltaLabel: '+ 2 novos este mês',
  },
  activities: {
    total: 42,
    segments: [
      { key: 'done', label: '28 concluídas', count: 28, color: '#22C55E' },
      { key: 'progress', label: '10 em andamento', count: 10, color: '#EAB308' },
      { key: 'late', label: '4 atrasadas', count: 4, color: '#EF4444' },
    ],
  },
  deliveries: {
    total: 8,
    segments: [
      { key: 'done', label: '5 concluídas', count: 5, color: '#22C55E' },
      { key: 'validation', label: '2 em validação', count: 2, color: '#EAB308' },
      { key: 'late', label: '1 atrasada', count: 1, color: '#EF4444' },
    ],
  },
  requests: {
    total: 14,
    segments: [
      { key: 'done', label: '10 concluídas', count: 10, color: '#22C55E' },
      { key: 'progress', label: '3 em andamento', count: 3, color: '#EAB308' },
      { key: 'late', label: '1 atrasada', count: 1, color: '#EF4444' },
    ],
  },
  progressSeries: [
    { monthLabel: 'Mai', plannedPercent: 18, actualPercent: 15 },
    { monthLabel: 'Jun', plannedPercent: 30, actualPercent: 28 },
    { monthLabel: 'Jul', plannedPercent: 42, actualPercent: 40 },
    { monthLabel: 'Ago', plannedPercent: 55, actualPercent: 52 },
    { monthLabel: 'Set', plannedPercent: 64, actualPercent: 60 },
    { monthLabel: 'Out', plannedPercent: 72, actualPercent: 68 },
  ],
  progressTooltip: {
    monthLabel: 'Outubro 2026',
    plannedPercent: 72,
    actualPercent: 68,
  },
  milestones: [
    {
      id: 'm1',
      day: '30',
      month: 'OUT',
      title: 'Homologação do módulo financeiro',
      projectName: 'Implantação CRM',
      statusLabel: 'Em validação',
      statusTone: 'validation',
    },
    {
      id: 'm2',
      day: '05',
      month: 'NOV',
      title: 'Go-live do dashboard comercial',
      projectName: 'Dashboard Comercial',
      statusLabel: 'Em andamento',
      statusTone: 'progress',
    },
    {
      id: 'm3',
      day: '12',
      month: 'NOV',
      title: 'Entrega da integração ERP',
      projectName: 'Integração ERP',
      statusLabel: 'Planejado',
      statusTone: 'planned',
    },
    {
      id: 'm4',
      day: '20',
      month: 'NOV',
      title: 'Treinamento da equipe de vendas',
      projectName: 'Implantação CRM',
      statusLabel: 'Planejado',
      statusTone: 'planned',
    },
  ],
  featuredProjects: [
    {
      id: 'crm',
      name: 'Implantação CRM',
      summary: 'Configuração e capacitação do time comercial',
      progressPercent: 78,
      statusLabel: 'Em andamento',
      statusTone: 'progress',
      iconTone: 'blue',
    },
    {
      id: 'dashboard-comercial',
      name: 'Dashboard Comercial',
      summary: 'Indicadores e funil de vendas em tempo real',
      progressPercent: 65,
      statusLabel: 'Em andamento',
      statusTone: 'progress',
      iconTone: 'green',
    },
    {
      id: 'integracao-erp',
      name: 'Integração ERP',
      summary: 'Sincronização de pedidos e estoque',
      progressPercent: 42,
      statusLabel: 'Em análise',
      statusTone: 'analysis',
      iconTone: 'orange',
    },
    {
      id: 'portal-cliente',
      name: 'Portal do Cliente',
      summary: 'Área autenticada de acompanhamento',
      progressPercent: 91,
      statusLabel: 'Em andamento',
      statusTone: 'progress',
      iconTone: 'purple',
    },
    {
      id: 'app-mobile',
      name: 'App Mobile',
      summary: 'Aplicativo para consulta e pedidos',
      progressPercent: 100,
      statusLabel: 'Concluído',
      statusTone: 'done',
      iconTone: 'sky',
    },
  ],
  updates: [
    {
      id: 'u1',
      title: 'Solicitação #015 aprovada',
      detail: 'Ajuste no fluxo de aprovação de pedidos',
      whenLabel: 'Hoje, 14:32',
      tone: 'success',
    },
    {
      id: 'u2',
      title: 'Entrega da versão 2.1 disponível',
      detail: 'Implantação CRM · Homologação liberada',
      whenLabel: 'Hoje, 11:05',
      tone: 'info',
    },
    {
      id: 'u3',
      title: 'Novo documento compartilhado',
      detail: 'Manual_testes_homologacao.pdf',
      whenLabel: 'Ontem, 16:40',
      tone: 'neutral',
    },
    {
      id: 'u4',
      title: 'Marco concluído',
      detail: 'Configuração dos campos personalizados',
      whenLabel: 'Ontem, 09:18',
      tone: 'success',
    },
    {
      id: 'u5',
      title: 'Comentário em atividade',
      detail: 'Rafael Oliveira · Integração com ERP',
      whenLabel: '25/10, 17:22',
      tone: 'warning',
    },
  ],
  recentRequests: [
    {
      id: 'r1',
      number: '015',
      title: 'Ajuste no fluxo de aprovação',
      projectName: 'Implantação CRM',
      typeLabel: 'Melhoria',
      statusLabel: 'Aprovada',
      statusTone: 'approved',
      dateLabel: '27/10',
    },
    {
      id: 'r2',
      number: '014',
      title: 'Inclusão de campo de prioridade',
      projectName: 'Dashboard Comercial',
      typeLabel: 'Mudança',
      statusLabel: 'Em andamento',
      statusTone: 'progress',
      dateLabel: '26/10',
    },
    {
      id: 'r3',
      number: '013',
      title: 'Dúvida sobre permissões',
      projectName: 'Portal do Cliente',
      typeLabel: 'Dúvida',
      statusLabel: 'Respondida',
      statusTone: 'answered',
      dateLabel: '25/10',
    },
    {
      id: 'r4',
      number: '012',
      title: 'Adiar go-live em 1 semana',
      projectName: 'Integração ERP',
      typeLabel: 'Mudança',
      statusLabel: 'Em análise',
      statusTone: 'analysis',
      dateLabel: '24/10',
    },
  ],
};
